import Vue from 'vue'
import {dpApi, defaultRange, errorText, mitaName, QUELLEN, weekInfo} from '@/shared/dienstplan_api'

// Shared state of the Dienstplanung pages. Staged changes ("vorgemerkt") live
// here until they are saved as one draft change, so they survive navigation
// between the planner pages. Everything authoritative is checked again by the
// server on save, preview and publication.
export const dp = Vue.observable({
  basis: {loaded: false, loading: false, schemaMissing: false, settings: {}, mitarbeiter: [], gruppen: [], raster: [], modelle: [], struktur: null},
  range: defaultRange(),
  plan: null,
  feiertage: {},
  regionValid: true,
  region: '',
  planLoading: false,
  staged: {zuordnungen: {}, besetzungen: {}, vertretungen: {}, rotation: null},
  wochen: {}, // pattern bis id -> {name, phasen}
  // state of the assignment form (kept while navigating between pages)
  editor: {typ: 'CREW', ziel: {CREW: '', MACHINE: '', DIRECT: ''}, gewaehlt: null, preset: null, checked: [], paritaet: 'ODD', fokusWoche: ''},
})

export const zKey = (zielTyp, zielId, iso) => zielTyp + '|' + zielId + '|' + iso
export const bKey = (mita, iso) => mita + '|' + iso
export const vKey = (mita, datum) => mita + '|' + datum

// ---------- loading ----------

export function loadBasis (force) {
  if (dp.basis.loaded && !force) return Promise.resolve()
  dp.basis.loading = true
  return Promise.all([dpApi.settings(), dpApi.mitarbeiter(), dpApi.gruppen(), dpApi.raster(), dpApi.modelle()])
    .then(([settings, mitarbeiter, gruppen, raster, modelle]) => {
      Object.assign(dp.basis, {settings, mitarbeiter, gruppen, raster, modelle, loaded: true, loading: false, schemaMissing: false})
    }, res => {
      dp.basis.loading = false
      if (res && res.status === 409 && res.body && res.body.code === 'SCHEMA_MISSING') {
        dp.basis.schemaMissing = true
      }
      throw res
    })
}

// organisation tree of the "Mitarbeiter zuweisen" dialog, loaded once
export function loadStruktur () {
  if (dp.basis.struktur) return Promise.resolve()
  return dpApi.struktur().then(s => { dp.basis.struktur = s || [] })
}

export function loadPlan (range) {
  if (range) dp.range = {von: range.von, bis: range.bis}
  dp.planLoading = true
  return dpApi.plan(dp.range.von, dp.range.bis).then(res => {
    dp.plan = res.plan
    dp.range = {von: res.plan.von, bis: res.plan.bis}
    dp.feiertage = res.feiertage || {}
    dp.regionValid = res.region_valid
    dp.region = res.region
    dp.planLoading = false
    return res.plan
  }, res => {
    dp.planLoading = false
    throw res
  })
}

export function rasterWochen (bisId) {
  if (dp.wochen[bisId]) return Promise.resolve(dp.wochen[bisId])
  return dpApi.rasterWochen(bisId).then(res => {
    Vue.set(dp.wochen, bisId, res)
    return res
  })
}

// ---------- lookups ----------

export const gruppe = id => dp.basis.gruppen.find(g => g.id === id)
export const gruppeName = id => (gruppe(id) || {}).bezeichnung || 'Gruppe'
export const kolonnen = () => dp.basis.gruppen.filter(g => g.aktiv && (g.typ === 'KOLONNE' || g.typ === 'TEAM'))
export const maschinen = () => dp.basis.gruppen.filter(g => g.aktiv && g.typ === 'MASCHINE')
export const positionen = () => dp.basis.modelle.filter(m => m.aktiv).reduce((all, m) => all.concat(m.positionen), [])
export const position = id => positionen().find(p => p.id === id)
export const mitarbeiter = id => dp.basis.mitarbeiter.find(m => m.bis_id === id)
export const name = bisId => {
  const m = mitarbeiter(bisId)
  return mitaName(bisId, m ? 'Pers.-Nr. ' + m.pers_number : '')
}

// employees as a flat list in the order of the organisation tree: a header per
// unit (Kostenstelle › Mitarbeitergruppe), then its employees by name.
// Employees without an organisation unit are not offered.
export function mitarbeiterListe () {
  const known = new Set(dp.basis.mitarbeiter.map(m => m.bis_id))
  const seen = new Set()
  const res = []
  const walk = (nodes, pfad) => nodes.forEach(n => {
    if (n.node_type === 'user') return
    const p = pfad[pfad.length - 1] === n.text ? pfad : pfad.concat(n.text)
    const users = (n.children || []).filter(c => c.node_type === 'user' && known.has(c.id) && !seen.has(c.id))
    if (users.length) {
      const einheit = p.join(' › ')
      res.push({header: einheit})
      users.map(u => {
        seen.add(u.id)
        const m = mitarbeiter(u.id)
        return {value: u.id, text: name(u.id), pers: m ? m.pers_number : '', einheit}
      }).sort((a, b) => a.text.localeCompare(b.text)).forEach(e => res.push(e))
    }
    walk(n.children || [], p)
  })
  walk(dp.basis.struktur || [], [])
  return res
}

export function inPool (gruppeId, mita, woche) {
  const g = gruppe(gruppeId)
  const w = weekInfo(woche)
  if (!g || !w) return false
  return g.mitglieder.some(m => m.mita_bis_id === mita && m.gueltig_von <= w.sonntag && (!m.gueltig_bis || m.gueltig_bis >= w.montag))
}

// machine-eligible: in the pool of a machine of the position's model
export function machineEligible (positionId, mita, woche) {
  const pos = position(positionId)
  const model = pos && dp.basis.modelle.find(m => m.id === pos.modell_id)
  return !!model && model.gruppen.some(g => inPool(g.planungsgruppe_id, mita, woche))
}

export const modellVonMaschine = machineId => dp.basis.modelle.find(m => m.aktiv && m.gruppen.some(g => g.planungsgruppe_id === machineId))

// positionWoche previews the combined week of a driver position: for every
// day the matrix (ISO parity + weekday) names the machine, whose mapped
// pattern week supplies the times. Authoritative resolution is on the server.
export function positionWoche (positionId, iso) {
  const pos = position(positionId)
  const w = weekInfo(iso)
  const model = pos && dp.basis.modelle.find(m => m.id === pos.modell_id)
  const tage = []
  const probleme = []
  for (let i = 0; i < 7; i++) {
    const duties = model ? model.matrix.filter(z => z.fahrerposition_id === positionId && z.wochenparitaet === w.paritaet && z.wochentag === i + 1) : []
    if (duties.length > 1) probleme.push('Mehrere Maschinen am ' + ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'][i])
    if (!duties.length) {
      tage.push({zeit_soll: 0, soll_zeit: '00:00-00:00', pause_1: '', pause_2: '', valence_hours: 0, schicht: 'frei', maschine: ''})
      continue
    }
    const z = effZuordnung('MACHINE', duties[0].planungsgruppe_id, iso)
    if (!z) {
      probleme.push(gruppeName(duties[0].planungsgruppe_id) + ': Musterwoche fehlt')
      tage.push({zeit_soll: 0, soll_zeit: '00:00-00:00', pause_1: '', pause_2: '', valence_hours: 0, schicht: '?', maschine: duties[0].planungsgruppe_id})
      continue
    }
    tage.push(Object.assign({}, z.tage[i], {schicht: gruppeName(duties[0].planungsgruppe_id), maschine: duties[0].planungsgruppe_id}))
  }
  return {tage, probleme: [...new Set(probleme)]}
}

// ---------- saved / effective values ----------

export function savedZuordnung (zielTyp, zielId, iso) {
  return dp.plan ? dp.plan.zuordnungen.find(z => z.ziel_typ === zielTyp && z.ziel_id === zielId && z.iso_woche === iso) || null : null
}

export function savedBesetzung (mita, iso) {
  return dp.plan ? dp.plan.besetzungen.find(b => b.mita_bis_id === mita && b.iso_woche === iso) || null : null
}

export function savedVertretung (mita, datum) {
  return dp.plan ? dp.plan.vertretungen.find(v => v.mita_bis_id === mita && v.datum === datum) || null : null
}

// effective = staged change if any, otherwise the saved draft
export function effZuordnung (zielTyp, zielId, iso) {
  const s = dp.staged.zuordnungen[zKey(zielTyp, zielId, iso)]
  if (s) return s.loeschen ? null : s
  return savedZuordnung(zielTyp, zielId, iso)
}

export function effBesetzung (mita, iso) {
  const s = dp.staged.besetzungen[bKey(mita, iso)]
  if (s) return s.loeschen ? null : s
  return savedBesetzung(mita, iso)
}

export function effVertretung (mita, datum) {
  const s = dp.staged.vertretungen[vKey(mita, datum)]
  if (s) return s.loeschen ? null : s
  return savedVertretung(mita, datum)
}

export const isStagedZ = (zielTyp, zielId, iso) => !!dp.staged.zuordnungen[zKey(zielTyp, zielId, iso)]
export const isStagedB = (mita, iso) => !!dp.staged.besetzungen[bKey(mita, iso)]

// ---------- labels ----------

export function zuordnungLabel (z) {
  if (!z) return 'Nicht zugeordnet'
  const rname = z.raster_name || ((dp.basis.raster.find(r => r.bis_id === z.azra) || {}).name) || z.azra
  return rname + ' / W' + z.woche_nr
}

export function quelleLabel (b) {
  if (!b) return 'Keine Wochenquelle'
  switch (b.typ) {
    case 'CREW': return gruppeName(b.planungsgruppe_id)
    case 'MACHINE': return 'Maschinendienst / ' + ((position(b.fahrerposition_id) || {}).bezeichnung || 'Position')
    case 'DIRECT': return 'Direkt / ' + zuordnungLabel(effZuordnung('DIRECT', b.mita_bis_id, b.iso_woche))
    default: return QUELLEN[b.typ] || b.typ
  }
}

// ---------- staging ----------

const sameZ = (a, b) => !!a && !!b && a.azra === b.azra && a.phase_von === b.phase_von && a.woche_nr === b.woche_nr &&
  (!a.snapshot_hash || !b.snapshot_hash || a.snapshot_hash === b.snapshot_hash)
const sameB = (a, b) => !!a && !!b && a.typ === b.typ && (a.planungsgruppe_id || '') === (b.planungsgruppe_id || '') &&
  (a.fahrerposition_id || '') === (b.fahrerposition_id || '')

// stageZuordnung marks a pattern-week mapping (or with loeschen its removal).
// week: {azra, raster_name, phase_von, phase_bis, woche_nr, hash, tage}
export function stageZuordnung (zielTyp, zielId, iso, week) {
  const key = zKey(zielTyp, zielId, iso)
  const saved = savedZuordnung(zielTyp, zielId, iso)
  if (!week) {
    if (!saved) Vue.delete(dp.staged.zuordnungen, key)
    else Vue.set(dp.staged.zuordnungen, key, {ziel_typ: zielTyp, ziel_id: zielId, iso_woche: iso, loeschen: true, vorher: saved})
    return
  }
  const next = {ziel_typ: zielTyp, ziel_id: zielId, iso_woche: iso, azra: week.azra, raster_name: week.raster_name, phase_von: week.phase_von,
    phase_bis: week.phase_bis, woche_nr: week.woche_nr, snapshot_hash: week.hash, tage: week.tage, vorher: saved}
  if (sameZ(saved, next) && !saved.raster_geaendert) Vue.delete(dp.staged.zuordnungen, key)
  else Vue.set(dp.staged.zuordnungen, key, next)
}

// stageBesetzung sets the weekly source of an employee (null = remove).
export function stageBesetzung (mita, iso, quelle) {
  const key = bKey(mita, iso)
  const saved = savedBesetzung(mita, iso)
  if (!quelle) {
    if (!saved) Vue.delete(dp.staged.besetzungen, key)
    else Vue.set(dp.staged.besetzungen, key, {mita_bis_id: mita, iso_woche: iso, loeschen: true, vorher: saved})
    return
  }
  const next = Object.assign({mita_bis_id: mita, iso_woche: iso, planungsgruppe_id: '', fahrerposition_id: '', vorher: saved}, quelle)
  if (sameB(saved, next)) Vue.delete(dp.staged.besetzungen, key)
  else Vue.set(dp.staged.besetzungen, key, next)
}

export function stageVertretung (mita, datum, vertreten) {
  const key = vKey(mita, datum)
  const saved = savedVertretung(mita, datum)
  if (!vertreten) {
    if (!saved) Vue.delete(dp.staged.vertretungen, key)
    else Vue.set(dp.staged.vertretungen, key, {mita_bis_id: mita, datum, loeschen: true, vorher: saved})
    return
  }
  const next = Object.assign({mita_bis_id: mita, datum, bemerkung: '', vorher: saved}, vertreten)
  if (saved && saved.vertretener_bis_id === next.vertretener_bis_id && saved.bemerkung === next.bemerkung) Vue.delete(dp.staged.vertretungen, key)
  else Vue.set(dp.staged.vertretungen, key, next)
}

export function stageRotation (rotation) {
  dp.staged.rotation = rotation
}

export function dirtyCount () {
  const s = dp.staged
  return Object.keys(s.zuordnungen).length + Object.keys(s.besetzungen).length + Object.keys(s.vertretungen).length + (s.rotation ? 1 : 0)
}

export function discardStaged (filter) {
  for (const kind of ['zuordnungen', 'besetzungen', 'vertretungen']) {
    for (const [k, v] of Object.entries(dp.staged[kind])) {
      if (!filter || filter(kind, v)) Vue.delete(dp.staged[kind], k)
    }
  }
  if (!filter) dp.staged.rotation = null
}

// changeRows describes the staged changes for review tables.
export function changeRows (filter) {
  const rows = []
  const zielName = z => z.ziel_typ === 'DIRECT' ? name(z.ziel_id) : gruppeName(z.ziel_id)
  for (const z of Object.values(dp.staged.zuordnungen)) {
    if (filter && !filter('zuordnungen', z)) continue
    rows.push({art: 'Musterwoche', ziel: zielName(z), zielTyp: z.ziel_typ, woche: z.iso_woche, vorher: zuordnungLabel(z.vorher), nachher: z.loeschen ? 'Entfernt' : zuordnungLabel(z), ersetzt: !!z.vorher})
  }
  for (const b of Object.values(dp.staged.besetzungen)) {
    if (filter && !filter('besetzungen', b)) continue
    rows.push({art: 'Wochenquelle', ziel: name(b.mita_bis_id), woche: b.iso_woche, vorher: quelleLabel(b.vorher), nachher: b.loeschen ? 'Keine Wochenquelle' : quelleLabel(b), ersetzt: !!b.vorher})
  }
  for (const v of Object.values(dp.staged.vertretungen)) {
    if (filter && !filter('vertretungen', v)) continue
    rows.push({art: 'Tagesvertretung', ziel: name(v.mita_bis_id), woche: v.datum, vorher: v.vorher ? 'vertritt ' + name(v.vorher.vertretener_bis_id) : '–',
      nachher: v.loeschen ? 'Entfernt' : 'vertritt ' + name(v.vertretener_bis_id), ersetzt: !!v.vorher})
  }
  if (dp.staged.rotation && !filter) {
    rows.push({art: 'Rotation', ziel: gruppeName(dp.staged.rotation.planungsgruppe_id), woche: 'ab ' + dp.staged.rotation.anker_montag, vorher: '–', nachher: 'Rotationseinstellung', ersetzt: false})
  }
  return rows.sort((a, b) => (a.woche + a.ziel).localeCompare(b.woche + b.ziel))
}

// saveDraft sends all staged changes as one atomic draft change. On a
// concurrent change (409 DRAFT_CHANGED) the plan is reloaded and the staged
// changes are kept, so the user can review them against the new state.
export function saveDraft (zusammenfassung) {
  const s = dp.staged
  const strip = ({vorher, tage, raster_name, ...rest}) => rest // eslint-disable-line no-unused-vars
  const req = {
    basis_revision: dp.plan ? dp.plan.revision : 0,
    zusammenfassung: zusammenfassung || '',
    zuordnungen: Object.values(s.zuordnungen).map(strip),
    besetzungen: Object.values(s.besetzungen).map(strip),
    vertretungen: Object.values(s.vertretungen).map(strip),
    rotation: s.rotation,
  }
  return dpApi.entwurf(req).then(res => {
    discardStaged()
    return loadPlan().then(() => res.revision)
  }, res => {
    if (res && res.status === 409 && res.body && res.body.code === 'DRAFT_CHANGED') {
      return loadPlan().then(() => { throw res })
    }
    throw res
  })
}

export {errorText}

// warn before the browser drops unsaved staged changes
if (typeof window !== 'undefined' && !window.__dpUnloadGuard) {
  window.__dpUnloadGuard = true
  window.addEventListener('beforeunload', e => {
    if (dirtyCount() > 0) {
      e.preventDefault()
      e.returnValue = ''
    }
  })
}
